import React, { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

const deviceFAQs = {
  default: [
    { q: 'How much can I sell my device for?', a: 'Device values depend on the model, storage capacity, condition, and carrier lock status. Use our instant quote tool to get an accurate price in under 30 seconds. We offer competitive prices and a price match available.' },
    { q: 'What condition does my device need to be in?', a: 'We buy devices in good working condition, flawless, good, and fair. Your device should power on and function normally. You will receive a fair quote based on the actual condition.' },
    { q: 'How fast will I get paid after selling?', a: 'We process payments the same day we receive and inspect your device. Choose from PayPal, Zelle, Venmo, CashApp, check, or cash (in-store). Most sellers receive payment within 24 hours of shipping.' },
    { q: 'Is shipping free when I sell my device?', a: 'Yes! We provide a FREE prepaid UPS shipping label. Simply pack your device, attach the label, and drop it off at any UPS location. Your device is insured during transit.' },
    { q: 'Do I need to factory reset my device before selling?', a: 'Yes, please factory reset your device, remove all accounts (iCloud, Google, Samsung), remove the SIM card, and disable any screen locks or PINs before shipping. This protects your personal data and speeds up processing.' },
    { q: 'What if I disagree with the final offer?', a: 'If the final offer after inspection differs from the initial quote, you can accept the revised offer or request your device back at no cost. We ship it back to you free of charge.' },
    { q: 'How does 2A Electronics compare to other buyback services?', a: '2A Electronics consistently offers higher payouts than Apple Trade-In, Samsung Trade-In, Gazelle, and Decluttr. We also offer same-day payment, free shipping, and fast, fair quotes on devices in good working condition.' },
    { q: 'Can I sell multiple devices at once?', a: 'Absolutely! You can sell as many devices as you want. Each device gets its own quote and tracking number. Bulk sellers may qualify for additional bonuses.' },
  ],
  phones: [
    { q: 'How much is my phone worth?', a: 'Phone values vary by model, storage, condition, and carrier. An iPhone 15 Pro Max in excellent condition can fetch up to $850, while a Samsung Galaxy S24 Ultra can get up to $700. Get your exact quote instantly on our website.' },
    { q: 'What condition should my phone be in?', a: 'We buy phones in good working condition that power on and function normally. Minor wear is fine, and the price is adjusted based on the actual condition.' },
    { q: 'What phones do you buy?', a: 'We buy all major phone brands: Apple iPhone (6 through 16 Pro Max), Samsung Galaxy (S, Note, Z Fold, Z Flip, A series), Google Pixel, OnePlus, Motorola, LG, and more. If it has value, we will buy it.' },
    { q: 'Do I need to unlock my phone before selling?', a: 'No, we buy both unlocked and carrier-locked phones. However, unlocked phones typically receive higher offers. Please remove iCloud lock and Google FRP before selling.' },
    { q: 'How long does the phone selling process take?', a: 'The entire process takes 3-5 business days from start to payment: get a quote (instant), ship your phone (1-3 days), inspection and payment (same day upon receipt).' },
    { q: 'Where can I sell my phone near me?', a: 'You can visit our Chicago store at 4859 W Irving Park Rd for instant cash, or use our nationwide mail-in service from anywhere in the USA with free shipping.' },
  ],
  laptops: [
    { q: 'How much can I sell my laptop for?', a: 'Laptop values depend on brand, model, specs, and condition. A MacBook Pro M3 in good condition can fetch up to $1,200. Dell, HP, Lenovo, and ASUS laptops are also accepted.' },
    { q: 'What condition should my laptop be in?', a: 'We buy laptops in good working condition that power on and run normally. Your offer reflects the actual condition and specs.' },
    { q: 'What laptop brands do you buy?', a: 'We buy all major brands: Apple MacBook (Air, Pro), Dell (XPS, Inspiron, Latitude), HP (Spectre, Envy, Pavilion), Lenovo (ThinkPad, Yoga, IdeaPad), ASUS, Microsoft Surface, and more.' },
    { q: 'Should I wipe my laptop before selling?', a: 'Yes, please back up your data and perform a factory reset. For MacBooks, sign out of iCloud and erase all content. For Windows laptops, use the built-in reset feature. We also perform a certified data wipe for added security.' },
  ],
  tablets: [
    { q: 'How much is my iPad worth?', a: 'iPad values depend on the model, generation, storage, and condition. An iPad Pro M2 in excellent condition can get up to $600, while an iPad Air 5th Gen can fetch around $350.' },
    { q: 'Do you buy tablets other than iPads?', a: 'Yes! We buy Samsung Galaxy Tabs, Microsoft Surface tablets, Amazon Fire tablets, and other Android tablets in addition to all iPad models.' },
    { q: 'What condition should my tablet be in?', a: 'We buy tablets in good working condition that power on and function normally. The price is adjusted based on the actual condition.' },
  ],
  consoles: [
    { q: 'How much can I sell my PS5 for?', a: 'A PS5 in good condition can fetch up to $300, while a PS5 Digital Edition gets around $250. Price varies based on condition and included accessories.' },
    { q: 'Do you buy consoles without controllers?', a: 'Yes, we buy consoles with or without controllers and cables. Having original accessories can increase your offer, but they are not required.' },
    { q: 'What gaming consoles do you buy?', a: 'We buy PlayStation (PS5, PS4, PS3), Xbox (Series X, Series S, One X, One S), and Nintendo (Switch OLED, Switch, Switch Lite, 3DS) consoles.' },
  ],
  watches: [
    { q: 'How much is my Apple Watch worth?', a: 'Apple Watch values depend on the model and condition. An Apple Watch Ultra 2 in excellent condition can get up to $450, while a Series 9 can fetch around $250.' },
    { q: 'What condition should my smartwatch be in?', a: 'We buy smartwatches in good working condition that power on and function normally. The offer is adjusted based on the actual condition.' },
  ],
};

const SellFAQSection = ({ category = 'default', customFAQs = null, deviceName = '' }) => {
  const [openIndex, setOpenIndex] = useState(null);
  const faqs = customFAQs || [...(deviceFAQs[category] || []), ...deviceFAQs.default];

  useEffect(() => {
    const script = document.getElementById('sell-faq-schema');
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.a,
        },
      })),
    };

    if (script) {
      script.textContent = JSON.stringify(faqSchema);
    } else {
      const newScript = document.createElement('script');
      newScript.id = 'sell-faq-schema';
      newScript.type = 'application/ld+json';
      newScript.textContent = JSON.stringify(faqSchema);
      document.head.appendChild(newScript);
    }

    return () => {
      const el = document.getElementById('sell-faq-schema');
      if (el) el.remove();
    };
  }, [faqs]);

  return (
    <section className="py-16" data-testid="sell-faq-section">
      <div className="max-w-3xl mx-auto px-4">
        <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-2">
          Frequently Asked Questions
        </h2>
        <p className="text-slate-400 text-center mb-10">
          {deviceName ? `Everything you need to know about selling your ${deviceName}` : 'Everything you need to know about selling your device'}
        </p>

        <div className="space-y-3">
          {faqs.slice(0, 8).map((faq, i) => (
            <div
              key={i}
              className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden transition-colors hover:border-slate-700"
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between p-5 text-left"
                data-testid={`faq-item-${i}`}
              >
                <span className="text-white font-medium pr-4">{faq.q}</span>
                <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform duration-200 ${openIndex === i ? 'rotate-180' : ''}`} />
              </button>
              {openIndex === i && (
                <div className="px-5 pb-5 text-slate-400 text-sm leading-relaxed border-t border-slate-800/50 pt-3">
                  {faq.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SellFAQSection;
